import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router';

import { getConfig } from 'src/global-config';

import { SplashScreen } from 'src/components/loading-screen/splash-screen';

import { useRole } from 'src/auth/hooks/use-role';
import { AuthGuard } from 'src/auth/guard/auth-guard';
import { HRAuthGuard } from 'src/auth/guard/hr-guard';
import { RoleGuard } from 'src/auth/guard/role-guard';
import { defaultSettingsTab, SETTINGS_TAB_ACTIONS } from 'src/auth/role-matrix';

import { paths } from '../paths';
import { usePathname } from '../hooks';
import { DashboardLayout } from './lazy-layouts';

// ----------------------------------------------------------------------

const BasicInfoPage = lazy(() => import('src/pages/organization/subtabs/basicInfo'));
const PlanBillingPage = lazy(() => import('src/pages/pricing/billing-tab-page'));
const SettingMembersPage = lazy(() => import('src/pages/organization/subtabs/members/organisms'));
const InvitationsPage = lazy(() => import('src/pages/organization/subtabs/invitations/organisms'));
const KnowledgeBasePage = lazy(() =>
  import('src/pages/organization/subtabs/knowledge-base/index').then((module) => ({
    default: module.default,
  }))
);
const OrgSettingLayoutV2 = lazy(() =>
  import('src/pages/organization/org-setting-layout-v2').then((module) => ({
    default: module.default,
  }))
);
const InterviewerPersonaPage = lazy(() =>
  import('src/pages/organization/subtabs/interviewer-persona/index').then((module) => ({
    default: module.default,
  }))
);
// const AgentBuilderPage = lazy(() =>
//   import('src/pages/organization/subtabs/interviewer-persona/agent-builder/index')
// );
const EmailTemplatesPage = lazy(() =>
  import('src/pages/organization/subtabs/email-templates').then((module) => ({
    default: module.default,
  }))
);
const JobBoardEmbedPage = lazy(() =>
  import('src/pages/organization/subtabs/job-board-embed').then((module) => ({
    default: module.default,
  }))
);
const FormTemplatesPage = lazy(() => import('src/pages/organization/subtabs/form-templates'));
const IntegrationsPage = lazy(() => import('src/pages/organization/subtabs/integrations'));
// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<SplashScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <OrgSettingLayoutV2>
      <SuspenseOutlet />
    </OrgSettingLayoutV2>
  </DashboardLayout>
);

/** Paths for each settings tab, keyed the same way as the role matrix's tab actions. */
const SETTINGS_TAB_PATHS: Record<keyof typeof SETTINGS_TAB_ACTIONS, string> = {
  info: paths.orgSettings.info,
  jobBoardEmbed: paths.orgSettings.jobBoardEmbed,
  integrations: paths.orgSettings.integrations,
  billing: paths.orgSettings.billing,
  members: paths.orgSettings.members,
  invitations: paths.orgSettings.invitations,
  knowledgeBase: paths.orgSettings.knowledgeBase,
  hiringTeam: paths.orgSettings.hiringTeam,
  formTemplates: paths.orgSettings.formTemplates,
  emailTemplates: paths.orgSettings.notifications.emailTemplates,
};

/**
 * /organization-settings lands on the first tab the member's capabilities allow (Basic Info for Administrators,
 * Members for Recruiters and Hiring Managers). A member with no tab at all is sent to the job board.
 */
function SettingsIndexRedirect() {
  const { can, isLoading } = useRole();
  if (isLoading) return null;
  const tab = defaultSettingsTab(can);
  return <Navigate to={tab ? SETTINGS_TAB_PATHS[tab] : paths.jobs.root} replace />;
}

//TODO: Optimize Re-rendering of JobNavData
export function getOrganizationSettingRoutes(): RouteObject[] {
  const CONFIG = getConfig();

  return [
    {
      path: 'organization-settings',
      element: CONFIG.auth.skip ? (
        dashboardLayout()
      ) : (
        <AuthGuard>
          <HRAuthGuard>{dashboardLayout()}</HRAuthGuard>
        </AuthGuard>
      ),
      children: [
        { element: <SettingsIndexRedirect />, index: true },
        {
          path: 'info',
          element: (
            <RoleGuard action={SETTINGS_TAB_ACTIONS.info}>
              <BasicInfoPage />
            </RoleGuard>
          ),
        },
        {
          path: 'job-board-embed',
          element: (
            <RoleGuard action={SETTINGS_TAB_ACTIONS.jobBoardEmbed}>
              <JobBoardEmbedPage />
            </RoleGuard>
          ),
        },
        {
          path: 'integrations',
          element: (
            <RoleGuard action={SETTINGS_TAB_ACTIONS.integrations}>
              <IntegrationsPage />
            </RoleGuard>
          ),
        },
        {
          path: 'billing',
          element: (
            <RoleGuard action={SETTINGS_TAB_ACTIONS.billing}>
              <PlanBillingPage />
            </RoleGuard>
          ),
        },
        {
          path: 'members',
          element: (
            <RoleGuard action={SETTINGS_TAB_ACTIONS.members}>
              <SettingMembersPage />
            </RoleGuard>
          ),
        },
        {
          path: 'invitations',
          element: (
            <RoleGuard action={SETTINGS_TAB_ACTIONS.invitations}>
              <InvitationsPage />
            </RoleGuard>
          ),
        },
        {
          path: 'knowledge-base',
          element: (
            <RoleGuard action={SETTINGS_TAB_ACTIONS.knowledgeBase}>
              <KnowledgeBasePage />
            </RoleGuard>
          ),
        },
        // {
        //   path: 'hiring-team',
        //   element: (
        //     <RoleGuard action={SETTINGS_TAB_ACTIONS.hiringTeam}>
        //       <InterviewerPersonaPage />
        //     </RoleGuard>
        //   ),
        // },
        // {
        //   path: 'hiring-team/agent-builder',
        //   element: (
        //     <RoleGuard action={SETTINGS_TAB_ACTIONS.hiringTeam}>
        //       <AgentBuilderPage />
        //     </RoleGuard>
        //   ),
        // },
        {
          path: 'form-templates',
          element: (
            <RoleGuard action={SETTINGS_TAB_ACTIONS.formTemplates}>
              <FormTemplatesPage />
            </RoleGuard>
          ),
        },
        {
          path: 'notification/email-templates',
          element: (
            <RoleGuard action={SETTINGS_TAB_ACTIONS.emailTemplates}>
              <EmailTemplatesPage />
            </RoleGuard>
          ),
        },
        {
          path: 'notification',
          element: <Navigate to={paths.orgSettings.notifications.emailTemplates} replace />,
        },
      ],
    },
  ];
}
